import {
  getRecipientCount,
  sendDriverNotifications,
  getBroadcastHistory,
} from '../../helpers/api/notifications';
import { apiErrorMessage } from '../../helpers/api';

export const NotificationsActionsTypes = {
  PREVIEW_BROADCAST_STARTED: 'NOTIFICATIONS/PREVIEW_BROADCAST_STARTED',
  PREVIEW_BROADCAST_SUCCESS: 'NOTIFICATIONS/PREVIEW_BROADCAST_SUCCESS',
  PREVIEW_BROADCAST_FAILURE: 'NOTIFICATIONS/PREVIEW_BROADCAST_FAILURE',
  SEND_BROADCAST_STARTED: 'NOTIFICATIONS/SEND_BROADCAST_STARTED',
  SEND_BROADCAST_SUCCESS: 'NOTIFICATIONS/SEND_BROADCAST_SUCCESS',
  SEND_BROADCAST_FAILURE: 'NOTIFICATIONS/SEND_BROADCAST_FAILURE',
  RESET_BROADCAST: 'NOTIFICATIONS/RESET_BROADCAST',
  HISTORY_STARTED: 'NOTIFICATIONS/HISTORY_STARTED',
  HISTORY_SUCCESS: 'NOTIFICATIONS/HISTORY_SUCCESS',
  HISTORY_FAILURE: 'NOTIFICATIONS/HISTORY_FAILURE',
  RECORD_LOCAL_BROADCAST: 'NOTIFICATIONS/RECORD_LOCAL_BROADCAST',
  HYDRATE_HISTORY_CACHE: 'NOTIFICATIONS/HYDRATE_HISTORY_CACHE',
};

// Sequence preview requests so a slow earlier response can't overwrite the
// count for the currently-selected city (stale responses are dropped in the
// reducer by comparing requestId).
let previewRequestSeq = 0;

// `xlOnlyFlags` lists the backend segments the send would cover (see
// `DriverSegmentOptions`). The segments are disjoint, so the reach of several is
// exactly the sum of their counts.
export const fetchBroadcastPreview = (homeCity, xlOnlyFlags = [false]) => async (dispatch) => {
  const requestId = ++previewRequestSeq;
  dispatch({ type: NotificationsActionsTypes.PREVIEW_BROADCAST_STARTED, payload: { requestId } });
  try {
    const counts = await Promise.all(xlOnlyFlags.map((xlOnly) => getRecipientCount(homeCity, xlOnly)));
    const count = counts.reduce((sum, c) => sum + c, 0);
    dispatch({ type: NotificationsActionsTypes.PREVIEW_BROADCAST_SUCCESS, payload: { requestId, count } });
    return { success: true, count };
  } catch (error) {
    const message = apiErrorMessage(error);
    dispatch({ type: NotificationsActionsTypes.PREVIEW_BROADCAST_FAILURE, payload: { requestId, error: message } });
    return { success: false, error: message };
  }
};

// The backend resolves the cohort and fans out the pushes; the dashboard only
// supplies the message + route. One request per backend segment in
// `xlOnlyFlags`, sent in order and never in parallel, so a failure stops before
// the next segment and the caller knows exactly which segments went out.
//
// Resolves to `{ success, sent, failedXlOnly, error }`: `sent` lists each
// segment that was queued (`{ xlOnly, queued }`), and `success` is true only
// when every segment was. A partial send (`sent` non-empty, `success` false)
// already reached real drivers, so the caller must not offer a plain retry of
// the whole send — that would push the delivered segment twice.
export const sendBroadcast =
  ({ homeCity, title, body, event, origin, destination, when, tripId, xlOnlyFlags = [false] }) =>
  async (dispatch) => {
    dispatch({ type: NotificationsActionsTypes.SEND_BROADCAST_STARTED });
    const sent = [];
    for (const xlOnly of xlOnlyFlags) {
      try {
        const result = await sendDriverNotifications({
          homeCity,
          title,
          body,
          event,
          origin,
          destination,
          when,
          tripId,
          xlOnly,
        });
        sent.push({ xlOnly, queued: result.queued });
      } catch (error) {
        const message = apiErrorMessage(error);
        dispatch({ type: NotificationsActionsTypes.SEND_BROADCAST_FAILURE, payload: message });
        return { success: false, sent, failedXlOnly: xlOnly, error: message };
      }
    }
    const queued = sent.reduce((sum, s) => sum + s.queued, 0);
    dispatch({ type: NotificationsActionsTypes.SEND_BROADCAST_SUCCESS, payload: { queued } });
    return { success: true, sent, failedXlOnly: null, error: null };
  };

export const resetBroadcast = () => (dispatch) => {
  dispatch({ type: NotificationsActionsTypes.RESET_BROADCAST });
};

// The sent-log's server-side source of truth: recent broadcasts, newest first.
export const fetchBroadcastHistory = (days = 7) => async (dispatch) => {
  dispatch({ type: NotificationsActionsTypes.HISTORY_STARTED });
  try {
    const broadcasts = await getBroadcastHistory(days);
    dispatch({ type: NotificationsActionsTypes.HISTORY_SUCCESS, payload: { broadcasts } });
    return { success: true, broadcasts };
  } catch (error) {
    const message = apiErrorMessage(error);
    dispatch({ type: NotificationsActionsTypes.HISTORY_FAILURE, payload: message });
    return { success: false, error: message };
  }
};

// Optimistically drop a just-sent broadcast into the log so it appears the
// instant the send succeeds, before the server refetch reconciles it. Shaped
// like a server history item (`_local` marks it as not-yet-confirmed).
export const recordLocalBroadcast = (item) => (dispatch) => {
  dispatch({ type: NotificationsActionsTypes.RECORD_LOCAL_BROADCAST, payload: item });
};

// Paint the log immediately on mount from the last cached server response,
// before the live fetch resolves (and so a reload still shows recent sends).
export const hydrateHistoryCache = (broadcasts) => (dispatch) => {
  dispatch({ type: NotificationsActionsTypes.HYDRATE_HISTORY_CACHE, payload: { broadcasts } });
};
